// Ad-funnel ↔ Tork signup bridge.
//
// Cold traffic opts in on a niche landing page (lp.tork.digitaldomination.ai)
// with their PHONE (no email) and is nurtured toward the $1 trial over SMS.
// The GoHighLevel contact id rides into Tork on the URL as `?cid=` (the nurture
// links are https://tork.digitaldomination.ai/trial?cid=<id>). We:
//   1. capture + persist that `cid`,
//   2. prefill the buyer's name/phone from the landing API,
//   3. fire a "trial started" signal once the $1 trial is paid so GoHighLevel
//      can tag the contact and pull them out of the nurture drip.
//
// Every call here is best-effort: the landing app is a separate origin (CORS is
// open on its side) and none of this may ever block or break the Tork flow.

const LP_API_BASE = "https://lp.tork.digitaldomination.ai";
const CID_KEY = "tork_cid";

export interface FunnelContact {
  firstName: string | null;
  phone: string | null;
}

// Pull `?cid=` off the current URL and remember it. The opaque GHL contact id is
// the primary match key — we never put the phone number itself in a URL. Safe to
// call on every page load; only writes when a cid is actually present.
export function captureCid(): void {
  if (typeof window === "undefined") return;
  try {
    const cid = new URL(window.location.href).searchParams.get("cid");
    if (cid) localStorage.setItem(CID_KEY, cid);
  } catch {
    // Malformed URL / storage unavailable — ignore.
  }
}

export function getCid(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(CID_KEY);
  } catch {
    return null;
  }
}

function clearCid(): void {
  try {
    localStorage.removeItem(CID_KEY);
  } catch {
    // ignore
  }
}

// Look up a funnel lead's name + phone from the landing API so we can prefill
// the signup. Returns null for direct (non-funnel) visitors or any failure.
export async function fetchFunnelContact(
  cid: string | null = getCid(),
): Promise<FunnelContact | null> {
  if (!cid) return null;
  try {
    const res = await fetch(
      `${LP_API_BASE}/api/contact?cid=${encodeURIComponent(cid)}`,
    );
    if (!res.ok) return null;
    const d = await res.json();
    if (!d || !d.ok) return null;
    return {
      firstName: typeof d.first_name === "string" ? d.first_name : null,
      phone: typeof d.phone === "string" ? d.phone : null,
    };
  } catch {
    return null;
  }
}

// Tell GoHighLevel the trial was started (paid) so it can tag the contact
// (`tork-trial-started`) and stop nurturing them. Fire-and-forget, guarded so it
// only fires once per Stripe session, and clears the stored cid on success.
export function notifyTrialStarted(params: {
  sessionId: string;
  phone?: string | null;
  email?: string | null;
  firstName?: string | null;
}): void {
  if (typeof window === "undefined" || !params.sessionId) return;

  const guardKey = `tork_trial_started_${params.sessionId}`;
  try {
    if (sessionStorage.getItem(guardKey)) return;
    sessionStorage.setItem(guardKey, "1");
  } catch {
    // sessionStorage unavailable — fall through and fire.
  }

  const cid = getCid() || undefined;
  try {
    fetch(`${LP_API_BASE}/api/trial-started`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        cid,
        phone: params.phone || undefined,
        email: params.email || undefined,
        first_name: params.firstName || undefined,
      }),
    })
      .then(() => clearCid())
      .catch(() => {
        // Network/CORS failure must never surface on the success page.
      });
  } catch {
    // ignore
  }
}
